<template>
  <AppLayout>
    <div ref="page" class="flex flex-1 flex-col" data-testid="quality-check-page" :data-view="view">
      <!-- The top bar: back link, title, BETA and one quiet line of context.
           It never changes with the state, so BETA is on every state. It must
           stay a DIRECT child of this column: a wrapper exactly its own height
           would leave `position: sticky` no room to stick. -->
      <QcPageHeader
        ref="head"
        :evaluation-id="evaluationId"
        :reference="reference"
        :address="address"
        :support-email="supportEmail"
        :checked-at="checkedAt"
      />

      <div class="mx-auto w-full max-w-7xl flex-1 px-4 pb-8 pt-4">
        <QcNotice
          v-if="startError"
          tone="error"
          icon="pi-exclamation-circle"
          class="mb-4"
          data-testid="qc-start-error"
        >
          {{ $t(`messages.evaluation.qualityCheck.startErrors.${startError === 'no_file' ? 'noFile' : 'generic'}`) }}
        </QcNotice>

        <!-- RED-373: the report may be about another property. A hint that
             stays on screen, never a block — the check runs either way. -->
        <QcNotice
          v-if="subjectMismatch"
          tone="warn"
          icon="pi-exclamation-triangle"
          class="mb-4 shadow-xs"
          data-testid="qc-subject-mismatch"
        >
          <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.subject.title') }}</strong>
          {{ $t('messages.evaluation.qualityCheck.subject.body') }}
        </QcNotice>

        <!-- ── file states ────────────────────────────────────────────── -->

        <QcDropZone
          v-if="view === 'no-file'"
          :rejection="rejectionText"
          :max-megabytes="maxMegabytes"
          :retention-days="retentionDaysMax"
          @file="onFile"
        />

        <div
          v-else-if="view === 'uploading'"
          class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs"
          data-testid="qc-uploading"
        >
          <h3 class="mb-1 text-lg font-semibold text-gray-800">{{ $t('messages.evaluation.qualityCheck.uploading.title') }}</h3>
          <p class="text-sm text-gray-500">{{ pendingName }}</p>
          <ProgressBar :value="progressPercent" :show-value="false" class="my-3 h-1.5!" />
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-xs text-gray-400">
              {{ megabytes(loadedBytes) }} / {{ megabytes(totalBytes) }} MB ·
              {{ $t('messages.evaluation.qualityCheck.uploading.keepOpen') }}
            </p>
            <Button
              :label="$t('messages.evaluation.qualityCheck.uploading.cancel')"
              severity="secondary"
              text
              size="small"
              class="w-auto!"
              data-testid="qc-upload-cancel"
              @click="cancelUpload"
            />
          </div>
        </div>

        <div
          v-else-if="view === 'reading'"
          class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs"
          data-testid="qc-reading"
        >
          <h3 class="mb-2.5 flex items-center gap-2 text-lg font-semibold text-gray-800">
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" aria-hidden="true"></span>
            {{ $t('messages.evaluation.qualityCheck.reading.title') }}
          </h3>
          <p class="text-sm text-gray-500">{{ $t('messages.evaluation.qualityCheck.reading.body') }}</p>
        </div>

        <!-- A file no run has checked yet. The dialog opened by itself on a
             live read; on a cold load it waits for this button. -->
        <template v-else-if="view === 'file-ready'">
          <QcNotice tone="info" icon="pi-shield" class="mb-4 shadow-xs" data-testid="qc-ready-notice">
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.ready.title') }}</strong>
            {{ $t('messages.evaluation.qualityCheck.ready.check') }}
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.ready.paid') }}</strong>
          </QcNotice>
          <QcFileCard
            :upload="upload"
            mode="ready"
            :support-email="supportEmail"
            :starting="starting"
            :removing="removing"
            @start="requestStart"
            @remove="removeFile"
          />
        </template>

        <!-- ── run states ─────────────────────────────────────────────── -->

        <QcWaitPanel
          v-else-if="view === 'queued' || view === 'processing'"
          :status="view"
          :elapsed-seconds="elapsedSeconds"
          :checks-done="check?.checksDone ?? null"
          :checks-total="check?.checksTotal ?? null"
        />

        <div
          v-else-if="view === 'timed-out'"
          class="rounded-lg border border-gray-200 border-l-[3px] border-l-red-500 bg-white px-4 py-3 text-sm text-gray-700 shadow-xs"
          role="alert"
          data-testid="qc-timeout"
        >
          <!-- Two different stops, two sentences: a run that never left the
               queue can be started again (the server retires the stuck row);
               a run that is processing is still working on the server. -->
          <template v-if="timedOutWhile === 'queued'">
            <p data-testid="qc-timeout-queued">{{ $t('messages.evaluation.qualityCheck.waiting.queuedTimeout') }}</p>
            <Button
              :label="$t('messages.evaluation.qualityCheck.file.rerun')"
              icon="pi pi-refresh"
              class="mt-3 w-auto!"
              :disabled="!upload || !canRestartStale"
              :loading="starting"
              data-testid="qc-timeout-restart"
              @click="requestStart"
            />
          </template>
          <p v-else data-testid="qc-timeout-processing">{{ $t('messages.evaluation.qualityCheck.waiting.timeout') }}</p>
        </div>

        <!-- `failed`: a plain Estonian reason, BETA still in the top bar, and a
             way to try again. Never a stack trace, never an English vendor
             message (§6). The engine's own sentence is kept inside v3's box:
             it is the only way the appraiser learns WHY. -->
        <div
          v-else-if="view === 'failed'"
          class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs"
          data-testid="qc-failed"
        >
          <div class="rounded-md border border-gray-200 border-l-[3px] border-l-red-500 bg-white px-4 py-3 text-sm text-gray-700">
            <p class="font-semibold text-gray-800">{{ $t('messages.evaluation.qualityCheck.failed.title') }}</p>
            <p v-if="check?.errorMessage" class="mt-1" data-testid="qc-failed-reason">{{ check.errorMessage }}</p>
            <i18n-t keypath="messages.evaluation.qualityCheck.failed.help" tag="p" scope="global" class="mt-1">
              <template #email>
                <a :href="`mailto:${supportEmail}`" class="text-blue-600 underline hover:text-blue-800">{{ supportEmail }}</a>
              </template>
            </i18n-t>
          </div>
          <div class="mt-3 flex flex-wrap gap-2">
            <Button
              :label="$t('messages.evaluation.qualityCheck.failed.retry')"
              icon="pi pi-refresh"
              class="w-auto!"
              :disabled="!upload"
              :loading="starting"
              data-testid="qc-retry"
              @click="requestStart"
            />
            <Button
              :label="$t('messages.evaluation.qualityCheck.file.remove')"
              icon="pi pi-trash"
              severity="danger"
              outlined
              class="w-auto!"
              :loading="removing"
              data-testid="qc-failed-remove"
              @click="removeFile"
            />
          </div>
        </div>

        <!-- completed, with or without findings -->
        <template v-else-if="view === 'empty' || view === 'completed'">
          <QcFileCard
            :upload="upload"
            mode="done"
            class="mb-4"
            :support-email="supportEmail"
            :starting="starting"
            :removing="removing"
            @start="requestStart"
            @remove="removeFile"
          />
          <QcNotice
            v-if="expiring"
            tone="warn"
            icon="pi-clock"
            class="mb-4 shadow-xs"
            data-testid="qc-expiring"
          >
            {{ $t('messages.evaluation.qualityCheck.expiring', deletesAtParts) }}
          </QcNotice>
          <!-- RED-341 D9: a run where some questions failed or were never sent
               is finished but not whole. Say how much was done and why, above
               everything else, so no one reads the list as the full check. -->
          <QcNotice
            v-if="partialNotice"
            tone="warn"
            icon="pi-exclamation-triangle"
            class="mb-4 shadow-xs"
            data-testid="qc-partial"
            :data-stop-reason="check.stopReason ?? ''"
          >
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.partial.title') }}</strong>
            {{ $t(partialBodyKey, { done: check.checksDone, total: check.checksTotal }) }}
          </QcNotice>
          <!-- RED-342: a run whose standards questions went out on half a
               standard (or on none) says so. The worst outcome is a confident
               answer that rests on part of the standard. -->
          <QcNotice
            v-if="standardsNotice"
            tone="warn"
            icon="pi-book"
            class="mb-4 shadow-xs"
            data-testid="qc-standards-missing"
          >
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.standards.missingTitle') }}</strong>
            {{ standardsNotice }}
          </QcNotice>
          <QcFindingsScreen
            v-if="view === 'completed'"
            :findings="findings"
            :checklist="checklist ?? []"
            :checks-total="checkedItemCount"
            :support-email="supportEmail"
            :resolve="resolveFinding"
            :send-feedback="sendFeedback"
            :clear-feedback="clearFeedback"
          />
          <div v-else class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-empty">
            <!-- A real and GOOD outcome that must not look like a failure or
                 like nothing ran — and the caveat is not optional: "no errors"
                 alone invites the reader to stop reading (§6.2). -->
            <!-- A partial run must never wear the green "nothing found"
                 heading: part of it did not run at all (RED-341 D9). -->
            <div
              v-if="isPartial"
              class="rounded-md border border-gray-200 border-l-[3px] border-l-amber-500 bg-white p-4 text-gray-700"
              data-testid="qc-empty-partial"
            >
              <h3 class="mb-1.5 flex items-center gap-2 text-lg font-semibold text-gray-800">
                <span class="pi pi-exclamation-triangle text-amber-500" aria-hidden="true"></span>
                {{ $t('messages.evaluation.qualityCheck.partial.title') }}
              </h3>
              <p class="text-sm">{{ $t(partialEmptyBodyKey, { done: check.checksDone, total: check.checksTotal }) }}</p>
            </div>
            <div v-else class="rounded-md border border-gray-200 border-l-[3px] border-l-emerald-500 bg-white p-4 text-gray-700">
              <h3 class="mb-1.5 flex items-center gap-2 text-lg font-semibold text-green-800">
                <span class="pi pi-check" aria-hidden="true"></span>
                {{ $t('messages.evaluation.qualityCheck.empty.title') }}
              </h3>
              <p class="text-sm">{{ $t('messages.evaluation.qualityCheck.empty.checked', { items: checkedItemCount }) }}</p>
            </div>
            <QcNotice tone="info" class="mt-3" data-testid="qc-empty-caveat">
              <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.empty.noteLead') }}</strong>
              {{ $t('messages.evaluation.qualityCheck.empty.note') }}
            </QcNotice>
          </div>
        </template>

        <!-- The file is gone (retention, or `Eemalda fail`); the findings are
             not (design-spec §6.3.4 / §10.3). Places in the report are gone
             with the file, so the cards stop quoting them. -->
        <template v-else-if="view === 'deleted'">
          <div class="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-deleted">
            <h3 class="mb-2.5 text-lg font-semibold text-gray-800">{{ $t('messages.evaluation.qualityCheck.deleted.title') }}</h3>
            <p class="mb-3 text-sm text-gray-500">{{ $t('messages.evaluation.qualityCheck.deleted.body') }}</p>
            <Button
              :label="$t('messages.evaluation.qualityCheck.deleted.upload')"
              icon="pi pi-upload"
              class="w-auto!"
              data-testid="qc-deleted-upload"
              @click="wantsNewFile = true"
            />
          </div>
          <!-- RED-373 (RED-371 #16): an incomplete run stays incomplete after
               its file is gone. The findings list is still here, so its two
               notices must be too — the empty box below has its own. -->
          <QcNotice
            v-if="partialNotice"
            tone="warn"
            icon="pi-exclamation-triangle"
            class="mb-4 shadow-xs"
            data-testid="qc-partial"
            :data-stop-reason="check.stopReason ?? ''"
          >
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.partial.title') }}</strong>
            {{ $t(partialBodyKey, { done: check.checksDone, total: check.checksTotal }) }}
          </QcNotice>
          <QcNotice
            v-if="standardsNotice"
            tone="warn"
            icon="pi-book"
            class="mb-4 shadow-xs"
            data-testid="qc-standards-missing"
          >
            <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.standards.missingTitle') }}</strong>
            {{ standardsNotice }}
          </QcNotice>
          <QcFindingsScreen
            v-if="findings.length"
            :findings="findings"
            :checklist="checklist ?? []"
            :checks-total="checkedItemCount"
            :support-email="supportEmail"
            :resolve="resolveFinding"
            :send-feedback="sendFeedback"
            :clear-feedback="clearFeedback"
            file-deleted
          />
          <div v-else class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-empty">
            <!-- A partial run must never wear the green "nothing found"
                 heading: part of it did not run at all (RED-341 D9). -->
            <div
              v-if="isPartial"
              class="rounded-md border border-gray-200 border-l-[3px] border-l-amber-500 bg-white p-4 text-gray-700"
              data-testid="qc-empty-partial"
            >
              <h3 class="mb-1.5 flex items-center gap-2 text-lg font-semibold text-gray-800">
                <span class="pi pi-exclamation-triangle text-amber-500" aria-hidden="true"></span>
                {{ $t('messages.evaluation.qualityCheck.partial.title') }}
              </h3>
              <p class="text-sm">{{ $t(partialEmptyBodyKey, { done: check.checksDone, total: check.checksTotal }) }}</p>
            </div>
            <div v-else class="rounded-md border border-gray-200 border-l-[3px] border-l-emerald-500 bg-white p-4 text-gray-700">
              <h3 class="mb-1.5 flex items-center gap-2 text-lg font-semibold text-green-800">
                <span class="pi pi-check" aria-hidden="true"></span>
                {{ $t('messages.evaluation.qualityCheck.empty.title') }}
              </h3>
              <p class="text-sm">{{ $t('messages.evaluation.qualityCheck.empty.checked', { items: checkedItemCount }) }}</p>
            </div>
          </div>
        </template>

        <!-- RED-373: the run's cost. The server sends `usd` to REdata admins
             only (RED-344 decision C: the appraiser never sees money), so for
             everyone else there is nothing here to hide. -->
        <p
          v-if="costLine"
          class="mt-6 text-xs text-gray-400"
          data-testid="qc-admin-cost"
        >
          {{ costLine }} {{ $t('messages.evaluation.qualityCheck.cost.adminOnly') }}
        </p>
      </div>

      <!--
        The bottom bar (prototype v3): how far the review has got, and the two
        ways on. Sticky rather than fixed, so it settles above the footer at the
        end of the page instead of covering it. Never rendered empty.
      -->
      <div
        v-if="barVisible"
        class="sticky bottom-0 z-30 flex flex-col items-stretch gap-2 border-t border-gray-200 bg-white px-4 py-2.5 shadow-[0_-1px_3px_rgb(0_0_0/0.05)] sm:flex-row sm:items-center sm:gap-3"
        data-testid="qc-action-bar"
      >
        <span v-if="reviewedTotal > 0" class="text-sm text-gray-500" data-testid="qc-bar-reviewed">
          {{ $t('messages.evaluation.qualityCheck.bar.reviewed', { done: reviewedDone, total: reviewedTotal }) }}
        </span>
        <span class="hidden flex-1 sm:block"></span>
        <span v-if="isWaitingView" class="text-sm text-gray-500" data-testid="qc-bar-waiting">
          {{ $t('messages.evaluation.qualityCheck.bar.waiting') }}
        </span>
        <template v-if="view === 'completed'">
          <Button
            :label="$t('messages.evaluation.qualityCheck.file.rerun')"
            icon="pi pi-refresh"
            severity="secondary"
            :loading="starting"
            data-testid="qc-bar-rerun"
            @click="requestStart"
          />
          <Button
            :label="$t('messages.evaluation.qualityCheck.back')"
            data-testid="qc-bar-back"
            @click="router.visit(route('evaluation.show', evaluationId))"
          />
        </template>
      </div>
    </div>

    <QcConfirmDialog
      :visible="confirmOpen"
      :file-name="upload?.originalFilename ?? ''"
      @confirm="confirmStart"
      @cancel="cancelStart"
    />
  </AppLayout>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import { format, parseISO } from 'date-fns'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import QcConfirmDialog from '@/components/evaluation/qc/QcConfirmDialog.vue'
import QcDropZone from '@/components/evaluation/qc/QcDropZone.vue'
import QcFileCard from '@/components/evaluation/qc/QcFileCard.vue'
import QcFindingsScreen from '@/components/evaluation/qc/QcFindingsScreen.vue'
import QcNotice from '@/components/evaluation/qc/QcNotice.vue'
import QcPageHeader from '@/components/evaluation/qc/QcPageHeader.vue'
import QcWaitPanel from '@/components/evaluation/qc/QcWaitPanel.vue'
import { useQcRunSummary } from '@/composables/useQcRunSummary'
import { useReportCheck } from '@/composables/useReportCheck'
import { useReportUpload } from '@/composables/useReportUpload'
import AppLayout from '@/Layouts/AppLayout.vue'

/**
 * RED-351 — the quality-check screen, variant A on its own route, rebuilt to
 * prototype v3 on 2026-09-24 (`.claude/tickets/RED-351/rework-2026-09-24.md`).
 *
 * Which screen shows is ONE computed `view`, derived from the `reportQc` prop
 * (the truth — nothing is kept in localStorage) plus the few things only the
 * browser knows: bytes in flight, a file being read, a give-up after six
 * minutes, and "the appraiser asked for the drop zone again".
 *
 * `replaced` (design-spec §6) does not exist here: a new file shows as
 * `file-ready` — the latest run belongs to an older file, so its result is not
 * shown against this one — and nothing runs until the cost dialog says so.
 */
const props = defineProps({
  evaluationId: { type: Number, required: true },
  objectId: { type: String, required: true },
  address: { type: String, default: null },
  /** The appraiser's own report number, for "Hindamine <nr>" in the top bar. */
  reference: { type: String, default: null },
})

const { t } = useI18n()

const {
  upload,
  maxBytes,
  retentionDaysMax,
  uploading,
  reading,
  removing,
  loadedBytes,
  totalBytes,
  rejection,
  send,
  cancel: cancelUpload,
  remove,
} = useReportUpload(props.objectId)

const {
  check,
  supportEmail,
  findings,
  checklist,
  isRunning,
  starting,
  timedOut,
  timedOutWhile,
  canRestartStale,
  startError,
  confirmOpen,
  elapsedSeconds,
  enterFileReady,
  requestStart,
  confirmStart,
  cancelStart,
  resetForNewFile,
  resolveFinding,
  sendFeedback,
  clearFeedback,
} = useReportCheck(props.objectId)

/** `Lae uus fail üles` after the file was deleted: show the drop zone again. */
const wantsNewFile = ref(false)
const pendingName = ref('')

/** Show a notice when the file goes within this many hours (design-spec §6). */
const EXPIRING_WITHIN_MS = 6 * 60 * 60 * 1000

/** The latest run checked THIS file (every new upload is a new row). */
const checkedThisFile = computed(
  () => check.value !== null && upload.value !== null && check.value.reportUploadId === upload.value.id,
)

const view = computed(() => {
  if (uploading.value) {
    return 'uploading'
  }
  if (reading.value) {
    return 'reading'
  }
  if (isRunning.value) {
    return timedOut.value ? 'timed-out' : check.value.status
  }
  if (upload.value) {
    if (!checkedThisFile.value) {
      return 'file-ready'
    }
    if (check.value.status === 'failed') {
      return 'failed'
    }
    return findings.value.length > 0 ? 'completed' : 'empty'
  }
  if (!wantsNewFile.value && check.value?.status === 'completed') {
    return 'deleted'
  }
  return 'no-file'
})

const isWaitingView = computed(() => view.value === 'queued' || view.value === 'processing')

const { isPartial, partialBodyKey, partialEmptyBodyKey, standardsNotice, checkedItemCount, costLine: runCostLine } = useQcRunSummary(check, checklist)

/**
 * The partial-run notice above the findings list. Not in the `deleted` view
 * without findings: its empty box carries the partial sentence itself.
 */
const partialNotice = computed(() =>
  isPartial.value && (view.value === 'completed' || (view.value === 'deleted' && findings.value.length > 0)),
)

/**
 * RED-373: the uploaded report did not seem to name this evaluation's
 * property. The run's own copy wins once it has checked this file (the upload
 * row is pruned within days); before a run, the upload's.
 */
const subjectMismatch = computed(() => {
  if (['uploading', 'reading', 'no-file'].includes(view.value)) {
    return false
  }
  if (checkedThisFile.value || view.value === 'deleted') {
    return check.value?.subjectMismatch === true
  }
  return upload.value?.subjectMismatch === true
})

/** RED-373: present only when the server sent `usd` — that is, to an admin. */
const costLine = computed(() =>
  // Only beside the run it belongs to — not under a new file no run has checked.
  ['completed', 'empty', 'failed', 'deleted'].includes(view.value) ? runCostLine.value : '',
)

const checkedAt = computed(() =>
  check.value?.status === 'completed' ? check.value.finishedAt ?? null : null,
)

// ── bottom bar ──────────────────────────────────────────────────────────────

const leidFindings = computed(() => findings.value.filter(f => f.type !== 'vaba'))
const reviewedTotal = computed(() =>
  view.value === 'completed' || view.value === 'deleted' ? leidFindings.value.length : 0,
)
const reviewedDone = computed(() => leidFindings.value.filter(f => f.resolution).length)
const barVisible = computed(() => isWaitingView.value || view.value === 'completed' || reviewedTotal.value > 0)

// ── file ────────────────────────────────────────────────────────────────────

// Same floor as `UploadLimit::maxMegabytes()`: a sub-megabyte cap reads "1 MB",
// never "0 MB", so the drop zone and the rejection message agree.
const maxMegabytes = computed(() => Math.max(1, Math.floor(Number(maxBytes.value ?? 0) / (1024 * 1024))))

const progressPercent = computed(() =>
  totalBytes.value > 0 ? Math.min(100, Math.round((loadedBytes.value * 100) / totalBytes.value)) : 0,
)

const megabytes = bytes => (Number(bytes ?? 0) / (1024 * 1024)).toFixed(1).replace('.', ',')

const deletesAtParts = computed(() => {
  const iso = upload.value?.deletesAt
  if (!iso) {
    return { date: '', time: '' }
  }
  const when = parseISO(iso)
  return { date: format(when, 'dd.MM.yyyy'), time: format(when, 'HH:mm') }
})

const expiring = computed(() => {
  const iso = upload.value?.deletesAt
  if (!iso) {
    return false
  }
  const left = parseISO(iso).getTime() - Date.now()
  return left > 0 && left < EXPIRING_WITHIN_MS
})

const rejectionText = computed(() => {
  if (!rejection.value) {
    return null
  }
  const key = {
    pdf: 'pdf',
    doc: 'doc',
    unsupported: 'unsupported',
    too_large: 'tooLarge',
    unreadable: 'unreadable',
    csrfExpired: 'csrfExpired',
  }[rejection.value.code] ?? 'generic'

  return t(`messages.detail.reportQc.errors.${key}`, { max: rejection.value.maxMegabytes ?? '' })
})

const onFile = async file => {
  resetForNewFile()
  pendingName.value = file.name
  const landed = await send(file)
  if (landed) {
    wantsNewFile.value = false
    // A LIVE read — the only thing that opens the cost dialog by itself.
    enterFileReady(true)
  }
}

const removeFile = async () => {
  await remove()
  resetForNewFile()
}

// ── the top bar's height, for the sticky index and the jump offset ─────────

const pageEl = useTemplateRef('page')
const headEl = useTemplateRef('head')
let headObserver = null

onMounted(() => {
  headObserver = new ResizeObserver(([entry]) => {
    pageEl.value?.style.setProperty('--qc-head-h', `${Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height)}px`)
  })
  const bar = headEl.value?.$el
  if (bar instanceof Element) {
    headObserver.observe(bar)
  }
})

onBeforeUnmount(() => headObserver?.disconnect())
</script>
